import { toUserFacingError } from '../utils/userFacingError';
import { fetchWithChatFeedback } from './chatFeedbackRequired';
import type { ProgressMessage } from '../hooks/useStageStatus';
const API_BASE = process.env.REACT_APP_API_URL || '';
export const CRR_TEMPLATE_UNAVAILABLE_MESSAGE = 'The CR&R checklist template is unavailable. An administrator needs to finish configuring it before you can check a contract.';

/** Recognized service states use fixed copy, never arbitrary exception details. */
export class DcmaCrrApiError extends Error {
  constructor(public readonly code: 'crr_template_unavailable', public readonly status: number) {
    super(CRR_TEMPLATE_UNAVAILABLE_MESSAGE);
    this.name = 'DcmaCrrApiError';
  }
}

export function dcmaCrrErrorMessage(error: unknown): string {
  return error instanceof DcmaCrrApiError ? CRR_TEMPLATE_UNAVAILABLE_MESSAGE : toUserFacingError(error);
}

export interface CrrEvidence {
  source_id: string;
  page: number;
  heading?: string | null;
  excerpt: string;
}
export interface CrrStructureResult {
  item_id: string;
  row: number;
  question: string;
  result: 'C' | 'X' | 'NA';
  reason_kind: string;
  information_found: string;
  evidence: CrrEvidence[];
  display_label?: string | null;
  display_result?: 'C' | 'X' | 'NA' | null;
  display_reason?: string | null;
  display_tone?: 'normal' | 'muted';
  display_visible?: boolean;
}
export interface CrrReview {
  status: 'complete' | 'partial';
  structure_results: CrrStructureResult[];
  clause_results: {
    item_id: string;
    target: string;
    present: boolean | null;
    sheet?: string;
    code?: string;
    mapping_kind?: 'spc' | 'r9' | 'other_kcr' | null;
    kcr_association?: 'known' | 'none' | 'unknown';
    mapping_provenance?: string;
    description?: string;
    reason: string;
    evidence: CrrEvidence[];
  }[];
  guidance: {
    item_id: string;
    question: string;
    answer: string;
    matched_codes?: string[];
    unresolved_codes?: string[];
    reason: string;
    evidence: CrrEvidence[];
  }[];
  required_family_results?: {
    family_id: string;
    label: string;
    status: 'located' | 'not_located' | 'unknown';
    clause_ids: string[];
    evidence: CrrEvidence[];
  }[];
  sam_comparison?: {
    status: 'matched' | 'field_mismatch' | 'not_found' | 'ambiguous' | 'unavailable' | 'not_checked';
    query: { cage?: string | null; uei?: string | null };
    contract: { name?: string | null; cage?: string | null; uei?: string | null; address?: string | null };
    entity?: { legal_name?: string | null; dba_name?: string | null; cage?: string | null; uei?: string | null; physical_address?: string | null; mailing_address?: string | null; registration_status?: string | null } | null;
    mismatches: string[];
    retrieved_at?: string | null;
    source_url?: string | null;
    api_version?: string | null;
    reason: string;
  } | null;
  warnings: string[];
}
export interface CrrSource {
  source_id: string;
  filename: string;
  role: 'contract' | 'supporting';
  sha256: string;
  size?: number;
}
export interface CrrRun {
  job_id: string;
  generation: number;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  phase?: string;
  status_message?: string;
  messages: ProgressMessage[];
  error?: {
    code: string;
    message: string;
  } | null;
  review?: CrrReview | null;
}
export interface CrrState {
  revision: number;
  expected_admin_dodaac?: string | null;
  expected_admin_origin?: 'user_supplied' | 'user_profile' | null;
  expected_admin_profile_revision?: number | null;
  sources: CrrSource[];
  run: CrrRun | null;
  runs: CrrRun[];
}
async function request(flow: string | null, path: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetchWithChatFeedback(`${API_BASE}/api/dcma-crr${flow === null ? '' : `/${encodeURIComponent(flow)}`}${path}`, {
    ...init,
    credentials: 'include'
  });
  if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    if (response.status === 403 && data?.detail?.code === 'dcma_crr_access_denied') window.dispatchEvent(new Event('dcma-crr-access-revoked'));
    if (data?.detail?.code === 'crr_template_unavailable') throw new DcmaCrrApiError('crr_template_unavailable', response.status);
    throw new Error(typeof data?.detail === 'string' ? data.detail : data?.detail?.message || 'The CR&R request could not be completed. Please reload and try again.');
  }
  return response;
}
export const getDcmaCrrReview = async (flow: string): Promise<CrrState> => (await request(flow, '/review')).json();
export async function uploadDcmaCrrSources(flow: string, contract: File | null, supporting: File[], revision = 0, expectedAdminDodaac?: string, useProfileDefault?: boolean, expectedProfileRevision?: number): Promise<CrrState> {
  const body = new FormData();
  if (contract) body.append('contract', contract);
  body.append('force_refresh', 'true');
  body.append('expected_revision', String(revision));
  if (expectedAdminDodaac !== undefined) body.append('expected_admin_dodaac', expectedAdminDodaac);
  if (useProfileDefault !== undefined) body.append('use_profile_default', String(useProfileDefault));
  if (expectedProfileRevision !== undefined) body.append('expected_profile_revision', String(expectedProfileRevision));
  supporting.forEach(file => body.append('supporting', file));
  return (await request(flow, '/sources', {
    method: 'POST',
    body
  })).json();
}
export const startDcmaCrrRun = async (flow: string, revision = 0, expectedAdminDodaac?: string, useProfileDefault?: boolean, expectedProfileRevision?: number): Promise<CrrState> => (await request(flow, '/runs', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    expected_revision: revision,
    force_refresh: true,
    ...(expectedAdminDodaac !== undefined ? { expected_admin_dodaac: expectedAdminDodaac } : {}),
    ...(useProfileDefault !== undefined ? { use_profile_default: useProfileDefault } : {}),
    ...(expectedProfileRevision !== undefined ? { expected_profile_revision: expectedProfileRevision } : {})
  })
})).json();
export const cancelDcmaCrrRun = async (flow: string, job: string): Promise<CrrRun> => (await request(flow, `/jobs/${encodeURIComponent(job)}/cancel`, {
  method: 'POST'
})).json();
export const getDcmaCrrJob = async (flow: string, job: string): Promise<CrrRun> => (await request(flow, `/jobs/${encodeURIComponent(job)}`)).json();
export const downloadDcmaCrrWorkbook = async (flow: string, job?: string): Promise<Blob> => (await request(flow, `/workbook${job ? `?job_id=${encodeURIComponent(job)}` : ''}`)).blob();
export const downloadDcmaCrrSource = async (flow: string, source: string): Promise<Blob> => (await request(flow, `/sources/${encodeURIComponent(source)}`)).blob();

export const deleteDcmaCrrSource = async (flow: string, source: string, revision = 0): Promise<CrrState> => (await request(flow, `/sources/${encodeURIComponent(source)}`, {
  method: 'DELETE',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ expected_revision: revision })
})).json();

export interface CrrProfile { admin_dodaac: string | null; revision: number; updated_at: string | null }
export const getDcmaCrrProfile = async (): Promise<CrrProfile> => (await request(null, '/profile')).json();
export const saveDcmaCrrProfile = async (adminDodaac: string, revision: number): Promise<CrrProfile> => (await request(null, '/profile', {
  method: 'PUT', headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ admin_dodaac: adminDodaac || null, expected_revision: revision })
})).json();
